<script setup lang="ts">
    import KButton from "@/components/Button.vue";
    import KButtonGroup from "@/components/ButtonGroup.vue";
    import { useStore } from "@/store";
    import { NLL_CONSENT_TYPES, NLLConsentType, NllSamtycke } from "@/tabs/Receptforskrivning/types";
    import { getAge } from "@/utils/patient/patientData";
    import { onBeforeMount, onMounted, ref } from "vue";

    const store = useStore();

    const consentTypes = ref<NLLConsentType[]>([]);

    const selectedConsentType = ref<NLLConsentType | null>(null);

    const samtyckeAutocompleteRef = ref();

    const emit = defineEmits(["submit", "close"]);

    const submit = async () => {
        store.dispatch("nll/fetchPatientinfo", { accessConsent: selectedConsentType.value?.code });
        emit("submit");
    };

    const cancel = () => {
        emit("close");
    };

    const onConsentTypeSelected = (option: { value: string }) => {
        selectedConsentType.value = NLL_CONSENT_TYPES.get(option.value) ?? null;
    };

    const getConsentTypes = () => {
        const accessConsent = store.getters["nll/sessionData"]?.accessConsent;

        if (accessConsent && accessConsent !== NllSamtycke.Nodatkomst) {
            return [NLL_CONSENT_TYPES.get(NllSamtycke.Nodatkomst)];
        }

        return (
            NLL_CONSENT_TYPES.values()
                // @ts-ignore
                .toArray()
                .filter((consentType: NLLConsentType) => {
                    if (!consentType.assurance) {
                        return false;
                    }

                    const age = getAge({ dateOfBirth: store.getters["patientData/patient"]?.dateOfBirth });
                    const isValidMinAge = !consentType.minAge || age >= consentType.minAge;
                    const isValidMaxAge = !consentType.maxAge || age <= consentType.maxAge;

                    return typeof age !== "number" || (isValidMinAge && isValidMaxAge);
                })
        );
    };

    onBeforeMount(() => {
        consentTypes.value = getConsentTypes();
    });

    onMounted(() => {
        samtyckeAutocompleteRef?.value?.setOptions({
            setDefault: true,
            options: consentTypes.value.map((consentType: NLLConsentType) => {
                return {
                    value: consentType.code,
                    label: consentType.display,
                };
            }),
        });
    });
</script>

<template>
    <div class="lagg-till-samtycke">
        <KAutocomplete
            ref="samtyckeAutocompleteRef"
            name="samtycke"
            label="Typ av samtycke"
            :options="[]"
            @inputValue="onConsentTypeSelected"
        />
        <div v-if="selectedConsentType" class="info-body selected-consent-type">
            <h3>{{ selectedConsentType.display }}</h3>
            <p>{{ selectedConsentType.information }}</p>
            <p class="additional-info" v-if="selectedConsentType.additionalInfo">
                {{ selectedConsentType.additionalInfo }}
            </p>
            <p>{{ selectedConsentType.assurance }}</p>
        </div>
        <div v-else class="info-body consent-type-summaries">
            <h3>Vilka typer av samtycken finns det?</h3>
            <div v-for="consentType in consentTypes" :key="consentType.code" class="consent-type-short">
                <label class="consent-type-label">{{ consentType.display }}</label>
                <p class="consent-type-summary">{{ consentType.summary }}</p>
            </div>
        </div>
        <KButtonGroup class="button-group">
            <KButton v-if="selectedConsentType" @click="submit" size="secondary">
                Lägg till samtycke och hämta förskrivningar från NLL
            </KButton>
            <KButton @click="cancel" size="slim" variant="contour">Avbryt</KButton>
        </KButtonGroup>
    </div>
</template>

<style lang="scss">
    .lagg-till-samtycke {
        .info-body {
            margin: 1.5rem 0 2.5rem 0;

            .consent-type-short {
                margin: 1.5rem 0;

                .consent-type-label {
                    font-weight: bold;
                    font-family: "Roboto medium";
                    margin: 0;
                }
            }
        }

        .selected-consent-type {
            p {
                margin: 1.5rem 0;
                white-space: pre-line;

                &.additional-info {
                    font-size: 14px;
                    line-height: 20px;
                }
            }
        }
    }
</style>
